import dynamic from 'next/dynamic'
import Head from 'next/head'
import Link from 'next/link'

const Layout = dynamic(() => import('../components/layout'))

export default function CancellationPolicy() {
  return (
    <Layout>
      <Head>
        <title>Terms and Conditions | Soluna London</title>
        <meta
          name="description"
          content="View terms and conditions. Industry leading skincare, facials, injectables, laser hair removal, bespoke semi-permanent makeup and much more in London. Call 0333 016 3500"
        />
        <link
          rel="canonical"
          href={`https://solunalondon.com/terms-and-conditions/`}
        />
        <link
          rel="alternate"
          hrefLang="en-GB"
          href={`https://solunalondon.com/terms-and-conditions/`}
        />
        <link
          rel="alternate"
          hrefLang="en"
          href={`https://solunalondon.com/terms-and-conditions/`}
        />
        <link
          rel="alternate"
          hrefLang="x-default"
          href={`https://solunalondon.com/terms-and-conditions/`}
        />
      </Head>
      <section className="my-16 md:my-16 max-w-4xl px-4 sm:mt-24 sm:px-6 text-gray-700 text-lg lg:max-w-7xl lg:mx-auto lg:px-8  lg:pr-64">
        <h1 className="font-serif">
          <span className="mt-1 block text-4xl font-medium sm:text-5xl xl:text-6xl capitalize">
            <span className="block text-gray-900">
              Terms and <span className="text-brand-600">Conditions</span>
            </span>
          </span>
        </h1>

        <div className="w-full lg:w-10/12 md:px-8 lg:pl-0 mt-12 text-gray-600">
          <article>
            <p className="mb-4">
              By proceeding with my booking I confirm that I understand and
              agree to the following terms and conditions:
            </p>

            <p className="mb-4">
              All clients must be 18 years old at the time of booking
            </p>

            <p className="mb-4 font-semibold text-gray-900">
              Booking fees are non-refundable in any circumstance.
            </p>

            <p className="mb-4">
              In the event an appointment is re-arranged or cancelled by
              Soluna London you will not be reimbursed for travel or further
              expenses that may have been incurred as a result.
            </p>

            <p className="mb-4">
              Please arrive 15 minutes prior to your scheduled appointment. If a
              client is over 15 minutes late their appointment will be cancelled
              and booking fee will be charged as a cancellation fee. You will
              need to pay a new booking fee for any future booking.
            </p>

            <p className="mb-4">
              If you are not able to provide ID if requested we will not proceed
              with treatment and you will forfeit your booking fee as they are
              non-refundable
            </p>

            <p className="mb-4">
              We do not allow children on the premises. If you arrive with
              anyone under the age of 18 we will not proceed with treatment and
              you will lose your booking fee
            </p>

            <p className="mb-4">
              We do not allow guests to accompany clients on the premises. If
              you arrive with a guest/s we will not proceed with treatment and
              you will lose your booking fee.
            </p>

            <p className="mb-4">
              We are not able to treat those who are medically unfit for
              treatment or are subject to/receiving treatment for any of the
              following condiitons
            </p>

            <ul className="list-inside list-disc my-8">
              <li>Pregnant</li>
              <li>Breastfeeding</li>
              <li>Major heart surgery</li>
              <li>Blood clotting</li>
              <li>Haemophilia</li>
              <li>Epilepsy</li>
              <li>Organ transplant</li>
              <li>Diabetes (Type 1)</li>
              <li>Hepatitis</li>
              <li>Lung disease </li>
              <li>Ashma</li>
              <li>Cancer</li>
              <li>Undergoing radiotherapy or chemotherapy</li>
              <li>Infectious disease</li>
              <li>Lupus</li>
              <li>Shingles</li>
              <li>HIV positive/AIDs</li>
              <li>Fever</li>
              <li>Iron deficiency</li>
              <li>Anaemia</li>
              <li>Respiratory problems</li>
              <li>Skin disease</li>
              <li>Hyperpigmentation</li>
              <li>Keloid scarring </li>
              <li>Alopecia</li>
              <li>Vitiligo</li>
              <li>Scars</li>
              <li>Herpes simplex</li>
              <li>Eczema</li>
              <li>Psoriasis</li>
              <li>
                Auto-immune conditions (these require doctors consent prior to
                treatment){' '}
              </li>
              <li>
                Roaccutane – Must allow 6 months after completing the course
                before having treatment{' '}
              </li>
              <li>Antabuse (must be finished course of medication) </li>
            </ul>

            <p className="mb-4">
              We cannot accept bookings made on behalf of someone else. We are
              not able to transfer bookings or booking fees to another
              individual.
            </p>

            <p className="mb-4">
              You can view our{' '}
              <Link href="/cancellation-policy/" passHref>
                cancellation policy here
              </Link>
            </p>

            <p className="mb-4">
              By completing a booking for an appointment, I acknowledge that my
              booking fee payment is in exchange for an allocated and reserved
              time slot of my choice, otherwise known as &#39;a service&#39;. I
              confirm that I have personally registered my details and reserved
              a time allocation that I am committed to. Should my circumstances
              change and I am no longer able to attend my booking I accept full
              liability and thus forfeit my booking fee payment to the company
              Soluna London. I understand that any attempts to withdraw this
              payment will result in legal action being taken against me. I
              understand that the evidence of my acknowledgement will be
              provided to my bank institution.
            </p>

            <p className="mb-4">
              All personal data will be stored safely and strictly in accordance
              with the Data Protection Act 2018.
            </p>

            <h2 className="text-xl font-serif text-gray-900 my-8">
              Terms And Conditions For Treatment Courses
            </h2>

            <p className="mb-4">
              Soluna London provides services to clients over a predetermined
              duration of time in a predetermined number and combination of
              treatment sessions that are combined to form a pre-paid course of
              treatment service.
            </p>

            <p className="mb-4">
              Courses attract a discounted rate per treatment session due to the
              upfront commitment of the client to complete the course of
              treatment in the agreed number of sessions over the agreed
              duration of time.
            </p>

            <p className="mb-4">
              We would recommend when booking or rescheduling your course
              sessions to do so as far in advance as you can, so we can
              facilitate your every need.
            </p>

            <p className="mb-4">
              Courses of treatment supplied by Soluna London have the
              following terms and conditions:
            </p>

            <ul className="list-inside list-disc my-8">
              <li>
                Soluna London is committed to exceeding your expectations
                with exceptional service throughout the course of your
                treatments.
              </li>
              <li>
                Soluna London endeavours to, on a best effort basis, hold
                each course session with the same aesthetic specialist. This is
                not a guarantee that the same aesthetic specialist will be
                available for every session. Wherever possible, attempts to
                rearrange the course session will be made to make available the
                original aesthetic specialist.
              </li>
              <li>
                Unfortunately, when booking or rescheduling course sessions
                within a shorter timeframe we cannot always guarantee that
                you&apos;ll be treated by the same aesthetic specialist or the
                exact time you desire for that individual appointment.
              </li>
              <li>
                When purchasing a course of treatments, you will avail of a
                special discounted rate.
              </li>
              <li>
                No reduction in price or alteration of a course of treatment is
                permitted once a course has been committed to and the first
                session has been held
              </li>
              <li>Courses of treatment are non-transferable to other people</li>
              <li>
                Course sessions follow the same terms and conditions as our
                <Link href="/cancellation-policy/" passHref>
                  cancellation policy here
                </Link>
              </li>
              <li>
                Missed course sessions will be forfeited and deducted from the
                course total of sessions with no exemptions or allowances made.
              </li>
              <li>
                Late attendance to course sessions will result in the session
                being cancelled, forfeited and deducted from the course total
                with no exemptions or allowances made. Late attendance is deemed
                as lateness in excess of 15 minutes from the scheduled start of
                the session.
              </li>
              <li>
                Photo ID and proof of address may be required during the
                purchase of a course of treatments
              </li>
              <li>
                Full payment or provision of a payment plan must be in place
                prior to any course of treatments beginning
              </li>
              <li>
                Treatments included within a course of treatment can not be
                amended or altered once the course has begun from the completion
                of the first session of the course.
              </li>
              <li>
                Our procedures and products may not be suitable for you and
                whilst all due care and skill is exercised in treating our
                clients ultimately it is your responsibility to determine if the
                product or treatment is right for you. In order for us to
                provide you with a great service it is fundamental that you
                provide all information requested in completing consent forms or
                advising us of any factors that may affect your treatments. You
                also need to have realistic expectations of the results of
                treatment. We encourage you to work with your aesthetic
                specialist on what you can achieve with your treatment.
              </li>
              <li>
                All purchases of a course of treatments have a 12 month expiry
                date. Once 12 months has been completed from the purchase date
                the course is deemed as being completed.
              </li>
              <li>
                All clients must be at least 18 years of age to avail of any of
                Soluna London&apos;s products and services
              </li>
              <li>
                Soluna London reserves the right to amend these terms and
                conditions at any given time
              </li>
            </ul>

            <h2 className="text-xl font-serif text-gray-900 my-8">
              Cancellation Of Treatment Courses
            </h2>

            <p className="mb-4">
              Courses of treatment supplied by Soluna London have the
              following terms and conditions with regards to cancellation:
            </p>

            <p className="mb-4">
              You have the right to cancel your prepaid purchase of a course of
              treatment:
            </p>

            <ul className="list-inside list-disc my-8">
              <li>Up until the first session has been held and completed</li>
              <li>
                Within fourteen days of purchase as long as a session has not
                been held and completed
              </li>
            </ul>

            <p className="mb-4 font-semibold text-black">
              Once a course of treatment has been purchased, the first session
              has been held and completed or fourteen days has elapsed from the
              date of purchase, the course of treatment is no longer refundable
              in any circumstance. No exemptions or allowances are available to
              this clause.
            </p>

            <h2 className="text-xl font-serif text-gray-900 my-8">
              Terms and Conditions for Promotional Treatments
            </h2>
            <ul className="list-inside list-disc my-8">
              <li>
                Payment Policy: During a promotional period, full payment must be completed by the end of the promotion.
                You can pay a booking fee to secure your reservation. However, if a booking fee is paid
                during the promotion and the full amount is not settled before the promotion concludes,
                you will be required to pay the original price of the selected treatment.
              </li>
            </ul>
            <ul className="list-inside list-disc my-8">
              <li>
                No Refunds or Exchanges: Treatments purchased during a promotional offer are non-refundable and non-exchangeable.
                Once you have purchased a promotional treatment, no refunds or exchanges will be permitted
                under any circumstances.
              </li>
            </ul>
            <ul className="list-inside list-disc my-8">
              <li>
                Completion of Treatment Plan: Once a promotional treatment is scheduled and/or started, the full treatment plan
                must be completed. Partial or unfinished treatments will not be refunded or carried
                to future sessions.
              </li>
            </ul>
            <ul className="list-inside list-disc my-8">
              <li>
                Expiration of Promotional Treatments: Promotional treatments must be completed within 12 months from the date of purchase.
                Any remaining sessions after 12 months from the purchase date will expire,
                and no extensions or substitutions will be provided.
              </li>
            </ul>
            <ul className="list-inside list-disc my-8">
              <li>
                Promotions cannot be combined with other offers under any circumstances.
              </li>
            </ul>
            
            <p className="mb-4">
              By purchasing promotional treatments, you acknowledge and agree to these terms
              and conditions. If you have any questions regarding these terms, please contact us
              before making your purchase.
            </p>
            
            <h2 className="text-xl font-serif text-gray-900 my-8">
              Our Journey with you
            </h2>

            <p className="mb-4">
              We want to keep you up to date with key information about
              promotional offers and new services. We will do this via email,
              post, mobile phone or social media. You have the right to ask us
              not to process your personal information for such purposes and you
              can change your mind at any time. If you have opted in, you can
              always remove your contact details from our list by opting out or
              emailing us at admin@solunalondon.com
            </p>

            <h2 className="text-xl font-serif text-gray-900 my-8">
              Payment Terms and Conditions with Klarna
            </h2>

            <p className="mb-4">
              In cooperation with Klarna Bank AB (publ), Sveavägen 46, 111 34
              Stockholm, Sweden, we offer you the following payment methods.
              Payment is to be made to Klarna.
            </p>

            <ul className="list-inside list-disc my-8">
              <li>
                Pay in 14 days: The payment period is 14 days from the
                availability date of the service. You can find the complete
                terms and conditions for the markets where this payment method
                is{' '}
                <a
                  rel="noopener noreferrer"
                  target="_blank "
                  href="https://cdn.klarna.com/1.0/shared/content/legal/terms/0/en_gb/pay_after_delivery"
                >
                  available here
                </a>
              </li>
              <li>
                Financing: With the financing service from Klarna you can pay
                your purchase in flexible or fixed monthly instalments according
                to the conditions stated in the checkout. The instalment payment
                is due at the end of each month after submission of a separate
                monthly invoice by Klarna. Further information regarding Slice
                It including terms and conditions and Standard European Consumer
                Credit Information you can{' '}
                <a
                  rel="noopener noreferrer"
                  target="_blank"
                  href="https://cdn.klarna.com/1.0/shared/content/legal/terms/0/en_gb/account_agreement"
                >
                  find here
                </a>
              </li>
              <li>
                Direct Debit: Your account will be debited after shipment of the
                goods or tickets/ availability date of the service ​or in case
                of a subscription in accordance with the timelines
                communicate​d. You will be notified about the date(s) by email.
              </li>
            </ul>
            <p className="mb-4">
              The payment methods Pay in [14] days, Slice It and direct debit
              are only available in case of a positive credit assessment. For
              this purpose, during the order process and handling of your
              purchase, we forward your data for an address and credit check to
              Klarna. We can only offer you the payment methods available based
              on the result of the credit check.
            </p>
            <p className="mb-4">
              The payment methods Pay in [14] days, Slice It and direct debit
              are only available in case of a positive credit assessment. For
              this purpose, during the order process and handling of your
              purchase, we forward your data for an address and credit check to
              Klarna. We can only offer you the payment methods available based
              on the result of the credit check. General information about
              Klarna and the user terms per country can be found on{' '}
              <a
                rel="noopener noreferrer"
                target="_blank"
                href="https://www.klarna.com/"
              >
                klarna.com
              </a>
              . Your personal data is handled in accordance with applicable data
              protection law and in accordance with the information in Klarnas
              privacy statement.
            </p>

            <p className="mb-4">
              In cooperation with{' '}
              <a
                rel="noopener noreferrer"
                target="_blank"
                href="https://www.klarna.uk/"
              >
                Klarna
              </a>{' '}
              Bank AB (publ), Sveavägen 46, 111 34 Stockholm, Sweden, we offer
              you the following payment options. Payment is to be made to
              Klarna:
            </p>

            <ul className="list-inside list-disc my-8">
              <li>Pay later.</li>
              <li>Financing.</li>
            </ul>

            <p className="mb-4">
              Further information and Klarnas user terms you can find{' '}
              <a
                rel="noopener noreferrer"
                target="_blank"
                href="https://cdn.klarna.com/1.0/shared/content/legal/terms/0/en_gb/user"
              >
                here
              </a>
              . General information on Klarna you can find{' '}
              <a href="https://www.klarna.com/uk/">here</a>. Your personal data
              is handled in accordance with applicable data protection law and
              in accordance with the information in{' '}
              <a
                rel="noopener noreferrer"
                target="_blank"
                href="https://cdn.klarna.com/1.0/shared/content/legal/terms/0/en_gb/privacy"
              >
                Klarnas privacy statement
              </a>
              .
            </p>

            <p className="mb-4">
              In order to be able to offer you Klarna’s payment options, we will
              pass to Klarna certain aspects of your personal information, such
              as contact and order details, in order for Klarna to assess
              whether you qualify for their payment options and to tailor the
              payment options for you.
            </p>

            <p className="mb-4">
              General information on Klarna you can find{' '}
              <a
                rel="noopener noreferrer"
                target="_blank"
                href="https://www.klarna.com/uk"
              >
                here
              </a>
              . Your personal data is handled in accordance with applicable data
              protection law and in accordance with the information in{' '}
              <a
                rel="noopener noreferrer"
                target="_blank"
                href="https://cdn.klarna.com/1.0/shared/content/legal/terms/Klarna/en_gb/privacy"
              >
                Klarna’s privacy policy
              </a>
              .
            </p>
            <div className="mt-12 space-y-4">
              <h3 className="text-xl font-serif text-gray-900 my-8">
                Price Match
              </h3>
              <p>
                The Price Match Gurantee only applies to competitors that
                operate within a 5 mile radius of Soluna London clinic the
                client is seeking to price match.
              </p>
              <p>
                We pride ourselves in delivering the best treatments & services
                with affordable price points to cater to all clients.
              </p>
              <p>
                Clients must provide proof of the competitor’s advertised price
                which must be current when seeking to price match and Soluna
                London may take further steps to confirm the validity of the
                price to be matched. Clients may only purchase the same number
                of treatments as advertised by the competitor using the Price
                Match Gurantee, and if no maximum number of treatments is listed
                the number of treatments able to be purchased are limited to 3
                per client.
              </p>
              <p>
                The Price Match Gurantee applies to all laser hair removal, skin
                services, cosmetic injectables. The Price Match Gurantee does
                not apply to prices offered by any competitors’ on retail
                products via an online shopping/retail website.
              </p>
              <p>
                The Price Match Gurantee does not apply to offers made by
                competitors from group buying, discount or coupon websites. The
                Price Match Gurantee will only apply to injectable treatments if
                the treatment requested by the client is necessary, indicated
                and recommended for the individual client by one of our
                practitioners following a full consultation and physical
                assessment.
              </p>
              <p>
                Our Price Match Gurantee does not apply to our already
                discounted Full Body Laser Hair Removal treatments.
              </p>
            </div>
            <div className="mt-12 space-y-4">
              <h3 className="text-xl font-serif text-gray-900 my-8">
                Attendance Policy
              </h3>
              <p>
                All appointments follow the same rules with no exceptions or
                amendments. It is imperative that you adhere to these conditions
                regarding attendace at your scheduled appointment
              </p>
              <ul className="list-inside list-disc">
                <li>
                  Attendance is for yourself and yourself only. No guests or
                  children will be permitted to attend with you at your
                  appointment. Children or guests will not be admitted to the
                  clinic
                </li>
                <li>
                  If you are more than 15 minutes late your appointment will be
                  cancelled. If this is the first time you have been late your
                  appointment may be rescheduled. If this is repeated late
                  attendance your appointment will be cancelled and booking fee
                  retained.
                </li>
              </ul>
            </div>
          </article>
        </div>
      </section>
    </Layout>
  );
}
